"use client";

import { useEffect, useId, useRef, useState } from "react";

import { Anchor, Compass, ExternalLink, GraduationCap, Sparkles, X } from "lucide-react";

import { cn } from "@louez/utils";
import { usePortIdentity } from "./port-identity";

function useNetworkSites() {
  const port = usePortIdentity();
  return PORT_NETWORK_SITES.map(site => site.id === "port" ? { ...site, label: port.name, href: `https://${port.slug}.com` } : site);
}

import {
  PORT_NETWORK_SITES,
  type PortNetworkSite,
  type PortNetworkSiteId,
} from "@/lib/port-network";

const SITE_ICONS = {
  port: Anchor,
  gestoria: Compass,
  licencia: GraduationCap,
} as const;

type PortNetworkSwitcherProps = {
  currentSite: PortNetworkSiteId;
};

export const PortNetworkSwitcher = ({ currentSite }: PortNetworkSwitcherProps) => {
  const sites = useNetworkSites();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const panelId = useId();
  const current = sites.find((site) => site.id === currentSite);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("mousedown", onPointerDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("mousedown", onPointerDown);
    };
  }, [open]);

  return (
    <div
      ref={rootRef}
      className="pointer-events-none fixed right-3 bottom-3 z-50 flex flex-col items-end gap-3 sm:right-5 sm:bottom-5"
    >
      <div
        id={panelId}
        className={cn(
          "origin-bottom-right transition-all duration-300 ease-out",
          open
            ? "pointer-events-auto translate-y-0 scale-100 opacity-100"
            : "pointer-events-none translate-y-3 scale-95 opacity-0",
        )}
        aria-hidden={!open}
      >
        <div className="mb-3 rounded-[1.75rem] border border-white/20 bg-slate-950/90 p-3 shadow-[0_30px_80px_-20px_rgba(8,25,45,0.85)] backdrop-blur-xl">
          <p className="px-2 pb-2 text-[11px] font-bold tracking-[0.18em] text-sky-200/80 uppercase">
            Puertos del Maresme
          </p>
          <div className="grid gap-2">
            {sites.map((site, index) => (
              <NetworkCard
                key={site.id}
                site={site}
                active={site.id === currentSite}
                delay={index * 60}
                open={open}
              />
            ))}
          </div>
        </div>
      </div>

      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((value) => !value)}
        className={cn(
          "pointer-events-auto group relative touch-manipulation overflow-hidden rounded-full border border-white/20 bg-slate-950/90 p-2.5 text-left text-white shadow-[0_20px_50px_-15px_rgba(14,116,144,0.65)] backdrop-blur-xl transition-transform hover:scale-[1.02] sm:px-4 sm:py-3",
          open && "ring-2 ring-sky-300/60",
        )}
      >
        <span className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(56,189,248,0.35),transparent_55%),radial-gradient(circle_at_80%_80%,rgba(45,212,191,0.25),transparent_50%)] opacity-80" />
        <span className="relative flex items-center gap-3">
          <span className="hidden size-10 items-center justify-center rounded-full bg-white/10 sm:flex">
            <Sparkles className="size-5 text-sky-200" />
          </span>
          <span className="hidden min-w-0 sm:block">
            <span className="block text-[10px] font-bold tracking-[0.16em] text-sky-200/80 uppercase">
              Cambiar sitio
            </span>
            <span className="block truncate text-sm font-semibold">
              {current?.label ?? "Port de Premià"}
            </span>
          </span>
          <span className="rounded-full bg-white/10 px-2 py-1 text-xs font-bold text-sky-100 sm:hidden">
            {open ? <X className="size-4" /> : "3"}
          </span>
        </span>
      </button>
    </div>
  );
};

type NetworkCardProps = {
  site: PortNetworkSite;
  active: boolean;
  delay: number;
  open: boolean;
};

const NetworkCard = ({ site, active, delay, open }: NetworkCardProps) => {
  const Icon = SITE_ICONS[site.id];

  const content = (
    <>
      <span
        aria-hidden
        className={cn(
          "absolute inset-0 bg-cover bg-center transition-transform duration-500",
          active ? "scale-100" : "group-hover:scale-105",
        )}
        style={{ backgroundImage: `url(${site.image})` }}
      />
      <span
        className={cn(
          "absolute inset-0 bg-gradient-to-br transition-opacity",
          site.accent,
          active ? "opacity-100" : "opacity-95 group-hover:opacity-100",
        )}
      />
      <span className="relative flex items-center gap-3 p-4">
        <span className="flex size-11 shrink-0 items-center justify-center rounded-2xl bg-white/15 backdrop-blur-sm">
          <Icon className="size-5 text-white" strokeWidth={1.8} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-bold text-white">{site.label}</span>
          <span className="mt-0.5 block text-xs leading-5 text-white/80">{site.subtitle}</span>
        </span>
        {active ? (
          <span className="rounded-full bg-white/20 px-2.5 py-1 text-[10px] font-bold tracking-[0.12em] text-white uppercase">
            Aquí
          </span>
        ) : (
          <ExternalLink className="size-4 shrink-0 text-white/80 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        )}
      </span>
    </>
  );

  const className = cn(
    "group relative block min-h-[5.5rem] overflow-hidden rounded-2xl border border-white/15 shadow-lg transition-all duration-300",
    site.glow,
    active ? "ring-2 ring-white/40" : "hover:-translate-y-0.5 hover:shadow-xl",
    open ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0",
  );

  const style = { transitionDelay: `${delay}ms` };

  if (active) {
    return (
      <div className={className} style={style}>
        {content}
      </div>
    );
  }

  return (
    <a href={site.href} className={className} style={style}>
      {content}
    </a>
  );
};

export const PortNetworkFooterLinks = ({ currentSite }: PortNetworkSwitcherProps) => {
 const sites = useNetworkSites();
 return (
  <nav aria-label="Puertos del Maresme">
    <p className="mb-3 text-xs font-semibold tracking-[0.1em] text-sky-200 uppercase">
      Ecosistema
    </p>
    <ul className="grid gap-2 text-sm">
      {sites.map((site) => (
        <li key={site.id}>
          {site.id === currentSite ? (
            <span className="font-semibold text-white">{site.label}</span>
          ) : (
            <a href={site.href} className="text-sky-100/80 hover:text-white hover:underline">
              {site.label}
            </a>
          )}
        </li>
      ))}
    </ul>
  </nav>
);
};
